import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import apiClient, { getClientIp } from '@/services/api'
import { useLoadingStore } from '@/stores/loadingStore'
import { useApiCodes } from '@/composables/useApiCodes'

export const useAuthStore = defineStore('auth', () => {
  const getStoredUser = () => {
    try {
      const stored = sessionStorage.getItem('user')
      return stored ? JSON.parse(stored) : null
    } catch (e) {
      return null
    }
  }

  const getStoredToken = () => sessionStorage.getItem('token')

  const user = ref(getStoredUser())
  const token = ref(getStoredToken())
  const isAuthenticated = computed(() => !!user.value)
  const isLoading = ref(false)
  const error = ref(null)
  const { getErrorCode, getSuccessMessage, getErrorMessage } = useApiCodes()

  const setUser = (userData, userToken = null) => {
    if (userData) {
      // Normalizza i campi provenienti dal backend (login, refresh, getProfile)
      user.value = {
        customerId: userData.customerId ?? userData.CustomerId,
        firstName: userData.firstName ?? userData.FirstName,
        lastName: userData.lastName ?? userData.LastName,
        email: userData.email ?? userData.Email,
        phone: userData.phone ?? userData.Phone ?? null,
        role: userData.role ?? userData.RoleName ?? userData.Role?.name,
        registrationDate: userData.registrationDate ?? userData.RegistrationDate,
        createdAt: userData.createdAt ?? userData.CreatedAt ?? userData.registrationDate ?? userData.RegistrationDate,
        isPrivacyAccepted: userData.isPrivacyAccepted ?? userData.IsPrivacyAccepted ?? false,
        privacyAcceptedAt: userData.privacyAcceptedAt ?? userData.PrivacyAcceptedAt ?? null,
        isMarketingAllowed: userData.isMarketingAllowed ?? userData.IsMarketingAllowed ?? false,
        marketingAllowedAt: userData.marketingAllowedAt ?? userData.MarketingAllowedAt ?? null
      }
      sessionStorage.setItem('user', JSON.stringify(user.value))
      if (userToken) {
        token.value = userToken
        sessionStorage.setItem('token', userToken)
      }
      // Rimuoviamo il flag di logout se l'utente si logga con successo
      localStorage.removeItem('user_logged_out')
    } else {
      user.value = null
    }
  }

  const clearUser = () => {
    user.value = null
    token.value = null
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('token')
  }

  const initializeFromSessionStorage = () => {
    const storedUser = sessionStorage.getItem('user')
    const storedToken = sessionStorage.getItem('token')
    if (storedUser) {
      try {
        user.value = JSON.parse(storedUser)
        token.value = storedToken
      } catch (err) {
        clearUser()
      }
    }
  }

  const register = async (email, password, firstName, lastName, phone = null, isPrivacyAccepted = false, isMarketingAllowed = false, recaptchaToken = null) => {
    isLoading.value = true
    error.value = null
    const loadingStore = useLoadingStore()
    loadingStore.show()
    try {
      const payload = {
        email,
        password,
        firstName,
        lastName,
        isPrivacyAccepted,
        isMarketingAllowed
      }
      if (phone) {
        payload.phone = phone
      }
      if (recaptchaToken) {
        payload.recaptchaToken = recaptchaToken
      }

      const response = await apiClient.post('/auth/register', payload)
      const { code } = response.data
      
      if (code && code !== 'AUTH_INVALID_REQUEST' && code !== 'AUTH_REGISTER_INVALID_DATA' && code !== 'AUTH_PRIVACY_REQUIRED' && code !== 'AUTH_EMAIL_ALREADY_VERIFIED') {
        return { success: true, code, data: response.data }
      }
      return { success: false, code: code || 'AUTH_INVALID_REQUEST', message: getErrorMessage(code || 'AUTH_INVALID_REQUEST'), data: response.data }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      console.error('[AUTHSTORE] Registration failed:', err)
      return { success: false, code, message: error.value, status: err.response?.status }
    } finally {
      isLoading.value = false
      loadingStore.hide()
    }
  }

const login = async (email, password, recaptchaToken = null) => {
     isLoading.value = true
     error.value = null
     const loadingStore = useLoadingStore()
     loadingStore.show()
     try {
       const payload = { email, password }
       if (recaptchaToken) {
         payload.recaptchaToken = recaptchaToken
       }
       const response = await apiClient.post('/auth/login', payload)
       
       // Actual API response format: { code, token, expiresIn, customer }
       const { code, token, customer } = response.data
       
       if (code === 'AUTH_LOGIN_SUCCESS' && token && customer) {
         setUser(customer, token)
         
         // Sincronizzazione carrello locale -> server (solo se non siamo in countdown)
         try {
           const { useConfigStore } = await import('@/stores/configStore')
           const configStore = useConfigStore()
           
           if (!configStore.isCountdownMode) {
             const { useCart } = await import('@/composables/useCart')
             const { syncCartWithServer } = useCart()
             await syncCartWithServer()
           }
         } catch (syncErr) {
           console.error('[AUTHSTORE] Cart sync failed after login:', syncErr)
         }
         
         return { success: true, code, data: response.data }
       } else {
         return { success: false, code: code || 'AUTH_INVALID_CREDENTIALS', data: response.data }
       }
     } catch (err) {
       const code = getErrorCode(err)
       error.value = getErrorMessage(code)
       return { success: false, code, message: error.value }
     } finally {
       isLoading.value = false
       loadingStore.hide()
     }
   }

  const getProfile = async () => {
    error.value = null
    try {
      const response = await apiClient.get('/Account/profile')
      // La risposta è { success, code, data: PersonalDataDto }
      const dto = response.data?.data || response.data
      setUser(dto)
      return { success: true, data: dto }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      console.error('[AUTHSTORE] Profile fetch error:', err)
      throw err
    }
  }

  const logout = async () => {
    isLoading.value = true
    error.value = null
    try {
      // Impostiamo un flag per evitare l'auto-auth al refresh dopo il logout
      localStorage.setItem('user_logged_out', 'true')

      // Pulizia carrello locale al logout
      try {
        const { useCart } = await import('@/composables/useCart')
        const { cartItems } = useCart()
        cartItems.value = []
        localStorage.removeItem('cart')
      } catch (cartErr) {
        console.error('[AUTHSTORE] Error clearing cart during logout:', cartErr)
      }

      await apiClient.post('/auth/logout')
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      clearUser()
      isLoading.value = false
    }
  }

  const verifyEmail = async (email, token, recaptchaToken = null) => {
    isLoading.value = true
    error.value = null
    try {
      const payload = { token }
      if (recaptchaToken) {
        payload.recaptchaToken = recaptchaToken
      }

      const response = await apiClient.post('/auth/verify-email', payload)
      
      const { code, customer } = response.data
      
      if (code === 'AUTH_EMAIL_VERIFIED' || code === 'AUTH_REGISTER_AUTO_VERIFIED') {
        if (customer) {
          setUser(customer)
          
          try {
            const { useConfigStore } = await import('@/stores/configStore')
            const configStore = useConfigStore()
            
            if (!configStore.isCountdownMode) {
              const { useCart } = await import('@/composables/useCart')
              const { syncCartWithServer } = useCart()
              await syncCartWithServer()
            }
          } catch (syncErr) {
            console.error('[AUTHSTORE] Cart sync failed after auto-login:', syncErr)
          }
        }
        return { success: true, code, data: response.data }
      }
      
      error.value = getErrorMessage(code)
      return { success: false, code: code || 'AUTH_TOKEN_INVALID', message: error.value, data: response.data }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const verifyNewsletter = async (token) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/newsletter/verify', {
        token
      })
      const { code } = response.data
      
      if (code === 'NEWSLETTER_VERIFIED') {
        return { success: true, code, data: response.data }
      }
      error.value = getErrorMessage(code)
      return { success: false, code: code || 'NEWSLETTER_TOKEN_INVALID', message: error.value, data: response.data }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const checkAuth = async () => {
    // Se abbiamo già l'utente in sessione, non facciamo nulla
    if (isAuthenticated.value) return true
    
    // Se l'utente ha fatto logout volontariamente, non tentiamo il refresh silente
    if (localStorage.getItem('user_logged_out') === 'true') return false

    try {
      // Tentiamo un refresh silente. Se il cookie è valido, l'API restituirà l'utente
      const response = await apiClient.post('/auth/refresh', {}, { withCredentials: true })
      const { code, customer, token } = response.data
      
      if ((code === 'AUTH_REFRESH_SUCCESS') && customer) {
        setUser(customer, token)
        return true
      }
    } catch (err) {
      // Refresh fallito o cookie scaduto, l'utente non è loggato
      return false
    }
    return false
  }

  const forgotPassword = async (email, recaptchaToken = null) => {
    isLoading.value = true
    error.value = null
    try {
      const payload = { email }
      if (recaptchaToken) {
        payload.recaptchaToken = recaptchaToken
      }
      const response = await apiClient.post('/auth/forgot-password', payload)
      const { code } = response.data
      
      if (code === 'AUTH_FORGOT_SUCCESS' || code === 'AUTH_EMAIL_REQUIRED') {
        return { success: true, code, data: response.data }
      }
      error.value = getErrorMessage(code)
      return { success: false, code: code || 'AUTH_INVALID_REQUEST', message: error.value, data: response.data }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const validateResetToken = async (token) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiClient.get('/auth/reset-password', {
        params: { token }
      })
      const { code } = response.data
      
      if (code === 'AUTH_TOKEN_VALID') {
        return { success: true, code, data: response.data }
      }
      error.value = getErrorMessage(code)
      return { success: false, code: code || 'AUTH_TOKEN_INVALID', message: error.value, data: response.data }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (token, newPassword, confirmPassword, recaptchaToken = null) => {
    isLoading.value = true
    error.value = null
    try {
      const payload = {
        token,
        newPassword,
        confirmPassword
      }
      if (recaptchaToken) {
        payload.recaptchaToken = recaptchaToken
      }
      const response = await apiClient.post('/auth/reset-password', payload)
      clearUser()
      const { code } = response.data
      
      if (code === 'AUTH_RESET_SUCCESS') {
        return { success: true, code, data: response.data }
      }
      error.value = getErrorMessage(code)
      return { success: false, code: code || 'AUTH_INVALID_REQUEST', message: error.value, data: response.data }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateProfile = async (firstName, lastName, phone, isMarketingAllowed) => {
    isLoading.value = true
    error.value = null
    try {
      const payload = {
        firstName: firstName?.trim() || null,
        lastName: lastName?.trim() || null,
        phone: phone?.trim() || null,
        isMarketingAllowed: !!isMarketingAllowed
      }

      await apiClient.put('/Account/profile', payload)
      
      // Ricarica il profilo completo dal backend per sincronizzare tutti i campi
      const profileResponse = await apiClient.get('/Account/profile')
      const dto = profileResponse.data?.data || profileResponse.data
      setUser(dto)
      
      return { success: true, code: 'ACCOUNT_UPDATE_SUCCESS', data: dto }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      console.error('[AUTHSTORE] Profile update error:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const changePassword = async (currentPassword, newPassword, confirmPassword) => {
    isLoading.value = true
    error.value = null
    try {
      const response = await apiClient.post('/account/change-password', {
        currentPassword,
        newPassword,
        confirmPassword
      })
      const { code } = response.data
      
      if (code === 'AUTH_CHANGE_SUCCESS') {
        return { success: true, code, data: response.data }
      }
      error.value = getErrorMessage(code)
      return { success: false, code: code || 'AUTH_INVALID_REQUEST', message: error.value, data: response.data }
    } catch (err) {
      const code = getErrorCode(err)
      error.value = getErrorMessage(code)
      console.error('[AUTHSTORE] Change password error:', err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    setUser,
    clearUser,
    initializeFromSessionStorage,
    register,
    login,
    logout,
    getProfile,
    verifyEmail,
    checkAuth,
    forgotPassword,
    validateResetToken,
    resetPassword,
    updateProfile,
    changePassword,
    verifyNewsletter
  }
})
